import React, { useEffect, useMemo, useState } from "react";
import API from "../../../services/api";
import SaveStudioAssetButton from "../SaveStudioAssetButton";
import FieldHint from "../FieldHint";
import useI18n from "../../../i18n/useI18n";

import MotionSurface from "../../../motion/studio/MotionSurface";
import ToolResultSkeleton from "../../loaders/ToolResultSkeleton";

import ResultCard from "../../ui/ResultCard";
import ActionRow from "../../ui/ActionRow";
import CopyButton from "../../ui/CopyButton";

import AIActionButton from "../../ui/buttons/AIActionButton";
import UtilityButton from "../../ui/buttons/UtilityButton";

const STUDIO_USAGE_REFRESH_KEY = "hookora_studio_usage_refresh";
const THEME_KEY = "hookora_theme";

const getPreferredLanguage = () => {
  if (typeof window === "undefined") return "en";
  return localStorage.getItem("hookora_language") || "en";
};

const notifyStudioUsageUpdated = () => {
  const value = String(Date.now());
  localStorage.setItem(STUDIO_USAGE_REFRESH_KEY, value);
  window.dispatchEvent(new CustomEvent("hookora:studio-usage-updated"));
};

const getScoreTone = (score) => {
  const safe = Number(score) || 0;
  if (safe >= 85) {
    return {
      bar: "#16a34a",
      pillBg: "rgba(22,163,74,0.14)",
      pillText: "#16a34a",
    };
  }
  if (safe >= 70) {
    return {
      bar: "#2563eb",
      pillBg: "rgba(37,99,235,0.14)",
      pillText: "#2563eb",
    };
  }
  return {
    bar: "#f59e0b",
    pillBg: "rgba(245,158,11,0.16)",
    pillText: "#b45309",
  };
};

const AIHumanizerTool = ({
  sharedTopic,
  setSharedTopic,
  sharedPlatform,
  setSharedPlatform,
}) => {
  const { t } = useI18n();

  const getStoredTheme = () => {
    if (typeof window === "undefined") return "light";
    return localStorage.getItem(THEME_KEY) || "light";
  };

  const [originalText, setOriginalText] = useState("");
  const [tone, setTone] = useState("Natural");
  const [audience, setAudience] = useState("");
  const [humanizationStrength, setHumanizationStrength] = useState("Balanced");
  const [preserveOriginalMeaning, setPreserveOriginalMeaning] = useState(true);
  const [styleNotes, setStyleNotes] = useState("");

  const [rewrites, setRewrites] = useState([]);
  const [error, setError] = useState("");
  const [copiedId, setCopiedId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [theme, setTheme] = useState(getStoredTheme());

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const syncTheme = () => setTheme(localStorage.getItem(THEME_KEY) || "light");
    window.addEventListener("storage", syncTheme);
    window.addEventListener("hookora-preferences-updated", syncTheme);

    return () => {
      window.removeEventListener("storage", syncTheme);
      window.removeEventListener("hookora-preferences-updated", syncTheme);
    };
  }, []);

  const isDark = theme === "dark";

  const colors = useMemo(
    () => ({
      cardBg: isDark ? "rgba(15,23,42,0.78)" : "#ffffff",
      cardBgStrong: isDark ? "rgba(15,23,42,0.92)" : "#ffffff",
      border: isDark ? "rgba(148,163,184,0.22)" : "#e5e7eb",
      borderStrong: isDark ? "rgba(148,163,184,0.34)" : "#d1d5db",
      text: isDark ? "#f8fafc" : "#111827",
      textMuted: isDark ? "#94a3b8" : "#6b7280",
      textSoft: isDark ? "#cbd5e1" : "#374151",
      inputBg: isDark ? "#0f172a" : "#ffffff",
      inputText: isDark ? "#f8fafc" : "#111827",
      trackBg: isDark ? "#1e293b" : "#e5e7eb",
      panelBg: isDark ? "rgba(30,41,59,0.55)" : "#f8fafc",
      buttonBg: isDark ? "#e2e8f0" : "#111827",
      buttonText: isDark ? "#020617" : "#ffffff",
      secondaryButtonBg: isDark ? "#1e293b" : "#e5e7eb",
      secondaryButtonText: isDark ? "#f8fafc" : "#111827",
      dangerBg: isDark ? "rgba(127,29,29,0.25)" : "#fef2f2",
      dangerText: isDark ? "#fecaca" : "#b91c1c",
      label: isDark ? "#e2e8f0" : "#111827",
      chipBg: isDark ? "rgba(30,41,59,0.9)" : "#f1f5f9",
      toggleBg: isDark ? "#0f172a" : "#ffffff",
      heroBg: isDark
        ? "linear-gradient(135deg, rgba(30,41,59,0.84), rgba(15,23,42,0.96))"
        : "linear-gradient(135deg, #f8fafc, #ffffff)",
      heroBorder: isDark ? "rgba(59,130,246,0.24)" : "#bfdbfe",
      heroBadgeBg: isDark ? "rgba(37,99,235,0.14)" : "#eff6ff",
      heroBadgeText: isDark ? "#bfdbfe" : "#1d4ed8",
      heroBadgeBorder: isDark ? "rgba(96,165,250,0.24)" : "#bfdbfe",
      shadow: isDark
        ? "0 16px 34px rgba(2,6,23,0.34)"
        : "0 12px 28px rgba(15,23,42,0.06)",
      shadowSoft: isDark
        ? "0 10px 24px rgba(2,6,23,0.22)"
        : "0 6px 18px rgba(15,23,42,0.05)",
    }),
    [isDark]
  );

  const inputStyle = {
    width: "100%",
    padding: "13px 14px",
    borderRadius: "12px",
    border: `1px solid ${colors.borderStrong}`,
    outline: "none",
    boxSizing: "border-box",
    background: colors.inputBg,
    color: colors.inputText,
    fontSize: "14px",
  };

  const textareaStyle = {
    ...inputStyle,
    minHeight: "110px",
    resize: "vertical",
  };

  const labelStyle = {
    display: "flex",
    alignItems: "center",
    marginBottom: "6px",
    color: colors.label,
    fontSize: "14px",
    fontWeight: 600,
  };

  const clearResults = () => {
    setRewrites([]);
    setCopiedId(null);
    setError("");
  };

  const resetForm = () => {
    setSharedTopic("");
    setSharedPlatform("General");
    setOriginalText("");
    setTone("Natural");
    setAudience("");
    setHumanizationStrength("Balanced");
    setPreserveOriginalMeaning(true);
    setStyleNotes("");
    setRewrites([]);
    setCopiedId(null);
    setError("");
  };

  const generateHumanizedVersions = async () => {
    if (!originalText.trim()) {
      setError(
        t(
          "studioTools.aiHumanizerTool.errors.originalTextRequired",
          "Please enter the original text first."
        )
      );
      return;
    }

    try {
      setLoading(true);
      setError("");

      const response = await API.post("/studio/humanizer", {
        original_text: originalText.trim(),
        tone,
        platform: sharedPlatform || "General",
        audience,
        humanization_strength: humanizationStrength,
        preserve_original_meaning: preserveOriginalMeaning,
        style_notes: styleNotes,
        language: getPreferredLanguage(),
      });

      setRewrites(Array.isArray(response?.data?.rewrites) ? response.data.rewrites : []);
      setCopiedId(null);
      notifyStudioUsageUpdated();
    } catch (err) {
      console.error("Failed to generate humanized rewrites:", err);
      setError(
        err?.response?.data?.detail ||
          t("studioTools.aiHumanizerTool.errors.generateFailed", "Failed to humanize text.")
      );
    } finally {
      setLoading(false);
    }
  };

  const copyRewrite = async (rewrite, id) => {
    try {
      await navigator.clipboard.writeText(rewrite?.text || "");
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error("Failed to copy rewrite:", err);
    }
  };

  const renderScoreBar = (score) => {
    const safe = Number(score) || 0;
    const toneColors = getScoreTone(safe);

    return (
      <div
        style={{
          marginTop: "10px",
          height: "10px",
          borderRadius: "999px",
          background: colors.trackBg,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${Math.max(0, Math.min(100, safe))}%`,
            height: "100%",
            borderRadius: "999px",
            background: toneColors.bar,
          }}
        />
      </div>
    );
  };

  return (
    <MotionSurface>
    <div style={{ display: "grid", gap: "18px" }}>
      <div
        style={{
          border: `1px solid ${colors.heroBorder}`,
          borderRadius: "20px",
          padding: "18px",
          background: colors.heroBg,
          boxShadow: colors.shadow,
        }}
      >
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            padding: "6px 10px",
            borderRadius: "999px",
            background: colors.heroBadgeBg,
            color: colors.heroBadgeText,
            border: `1px solid ${colors.heroBadgeBorder}`,
            fontSize: "12px",
            fontWeight: 800,
            letterSpacing: "0.03em",
            marginBottom: "12px",
          }}
        >
          {t("studioTools.aiHumanizerTool.hero.badge", "Rewrite polish tool")}
        </div>

        <div style={{ color: colors.text, fontSize: "24px", fontWeight: 800, marginBottom: "8px" }}>
          {t("studioTools.aiHumanizerTool.hero.title", "AI Humanizer")}
        </div>

        <div style={{ color: colors.textSoft, lineHeight: "1.7", maxWidth: "880px" }}>
          {t(
            "studioTools.aiHumanizerTool.hero.description",
            "Use this tool to turn stiff, robotic, or overly AI-sounding text into writing that feels more natural, believable, and human. Adjust tone, strength, and rewrite settings to get a version that feels right for the platform."
          )}
        </div>
      </div>

      <div
        style={{
          border: `1px solid ${colors.border}`,
          borderRadius: "18px",
          padding: "18px",
          background: colors.cardBg,
          boxShadow: colors.shadowSoft,
        }}
      >
        <div style={{ display: "grid", gap: "14px" }}>
          <div>
            <label style={labelStyle}>
              {t("studioTools.aiHumanizerTool.fields.originalText.label", "Original Text")}
              <FieldHint
                text={t(
                  "studioTools.aiHumanizerTool.fields.originalText.hint",
                  "Paste the AI-written or stiff copy you want to make sound more human, natural, and believable."
                )}
              />
            </label>
            <textarea
              value={originalText}
              onChange={(e) => setOriginalText(e.target.value)}
              placeholder={t(
                "studioTools.aiHumanizerTool.fields.originalText.placeholder",
                "Paste the original text you want to humanize"
              )}
              style={{ ...textareaStyle, minHeight: "180px" }}
            />
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: "12px",
            }}
          >
            <div>
              <label style={labelStyle}>
                {t("studioTools.aiHumanizerTool.fields.platform.label", "Platform")}
                <FieldHint
                  text={t(
                    "studioTools.aiHumanizerTool.fields.platform.hint",
                    "Choose where the rewrite will be used. This affects rhythm, phrasing, and style."
                  )}
                />
              </label>
              <select
                value={sharedPlatform}
                onChange={(e) => setSharedPlatform(e.target.value)}
                style={inputStyle}
              >
                {[
                  "General",
                  "Instagram",
                  "TikTok",
                  "X",
                  "LinkedIn",
                  "YouTube",
                  "WhatsApp",
                ].map((platform) => (
                  <option key={platform} value={platform}>
                    {t(`studioTools.aiHumanizerTool.platformOptions.${platform}`, platform)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={labelStyle}>
                {t("studioTools.aiHumanizerTool.fields.tone.label", "Tone")}
                <FieldHint
                  text={t(
                    "studioTools.aiHumanizerTool.fields.tone.hint",
                    "Choose the personality of the rewrite."
                  )}
                />
              </label>
              <select value={tone} onChange={(e) => setTone(e.target.value)} style={inputStyle}>
                {[
                  "Natural",
                  "Conversational",
                  "Bold",
                  "Professional",
                  "Warm",
                  "Confident",
                  "Persuasive",
                  "Story-driven",
                ].map((item) => (
                  <option key={item} value={item}>
                    {t(`studioTools.aiHumanizerTool.toneOptions.${item}`, item)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={labelStyle}>
                {t("studioTools.aiHumanizerTool.fields.audience.label", "Audience")}
                <FieldHint
                  text={t(
                    "studioTools.aiHumanizerTool.fields.audience.hint",
                    "Who the rewritten copy is for. This helps the output feel more relevant and human."
                  )}
                />
              </label>
              <input
                value={audience}
                onChange={(e) => setAudience(e.target.value)}
                placeholder={t("studioTools.aiHumanizerTool.fields.audience.placeholder", "Audience")}
                style={inputStyle}
              />
            </div>

            <div>
              <label style={labelStyle}>
                {t(
                  "studioTools.aiHumanizerTool.fields.humanizationStrength.label",
                  "Humanization Strength"
                )}
                <FieldHint
                  text={t(
                    "studioTools.aiHumanizerTool.fields.humanizationStrength.hint",
                    "Light keeps the structure close. Balanced improves flow. Strong rewrites more boldly while keeping the core message."
                  )}
                />
              </label>
              <select
                value={humanizationStrength}
                onChange={(e) => setHumanizationStrength(e.target.value)}
                style={inputStyle}
              >
                {["Light", "Balanced", "Strong"].map((item) => (
                  <option key={item} value={item}>
                    {t(`studioTools.aiHumanizerTool.strengthOptions.${item}`, item)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div
            style={{
              padding: "14px",
              borderRadius: "14px",
              background: colors.panelBg,
              border: `1px solid ${colors.border}`,
            }}
          >
            <div style={{ fontWeight: 700, color: colors.text, marginBottom: "12px" }}>
              {t("studioTools.aiHumanizerTool.sections.rewriteSettings", "Rewrite Settings")}
            </div>

            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                color: colors.text,
                fontSize: "14px",
                fontWeight: 600,
                marginBottom: "14px",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={preserveOriginalMeaning}
                onChange={(e) => setPreserveOriginalMeaning(e.target.checked)}
              />
              {t(
                "studioTools.aiHumanizerTool.fields.preserveOriginalMeaning.label",
                "Preserve original meaning"
              )}
            </label>

            <div>
              <label style={labelStyle}>
                {t("studioTools.aiHumanizerTool.fields.styleNotes.label", "Style Notes")}
                <FieldHint
                  text={t(
                    "studioTools.aiHumanizerTool.fields.styleNotes.hint",
                    "Add guidance like shorter sentences, more direct tone, less formal phrasing, more emotion, or fewer buzzwords."
                  )}
                />
              </label>
              <textarea
                value={styleNotes}
                onChange={(e) => setStyleNotes(e.target.value)}
                placeholder={t(
                  "studioTools.aiHumanizerTool.fields.styleNotes.placeholder",
                  "Style notes or rewrite guidance"
                )}
                style={textareaStyle}
              />
            </div>
          </div>

          {error && (
            <div
              style={{
                padding: "12px 14px",
                borderRadius: "12px",
                background: colors.dangerBg,
                color: colors.dangerText,
              }}
            >
              {error}
            </div>
          )}

          <ActionRow>
            <AIActionButton isDark={isDark}
              onClick={generateHumanizedVersions}
              disabled={loading}
            >
              {loading
                ? t("studioTools.aiHumanizerTool.actions.humanizing", "Humanizing...")
                : t("studioTools.aiHumanizerTool.actions.humanizeText", "Humanize Text")}
            </AIActionButton>

            <UtilityButton isDark={isDark}
              type="button"
              onClick={clearResults}
            >
              {t("studioTools.aiHumanizerTool.actions.clearResults", "Clear Results")}
            </UtilityButton>

            <UtilityButton isDark={isDark}
              type="button"
              onClick={resetForm}
            >
              {t("studioTools.aiHumanizerTool.actions.resetForm", "Reset Form")}
            </UtilityButton>
          </ActionRow>
        </div>
      </div>

      {!!rewrites.length && (
        <div
          style={{
            color: colors.textMuted,
            fontSize: "14px",
            lineHeight: "1.6",
            marginTop: "-4px",
          }}
        >
          {t(
            "studioTools.aiHumanizerTool.resultsIntro",
            "These are humanized rewrite options. Pick the one that feels most natural, most believable, and closest to your intended voice."
          )}
        </div>
      )}

      <div style={{ display: "grid", gap: "14px" }}>
        {loading ? (
          <ToolResultSkeleton isDark={isDark} count={3} />
        ) : (
        rewrites.map((rewrite, index) => {
          const toneColors = getScoreTone(rewrite.score);

          return (
            <ResultCard
              key={`${rewrite.version_name}-${index}`}
              colors={colors}
              index={index}
            >
              <div
                style={{
                  color: colors.text,
                  fontWeight: 800,
                  fontSize: "18px",
                  lineHeight: "1.4",
                  marginBottom: "10px",
                }}
              >
                {rewrite.version_name ||
                  t("studioTools.aiHumanizerTool.labels.alternative", {
                    defaultValue: "Alternative {{number}}",
                    number: index + 1,
                  })}
              </div>

              <div
                style={{
                  color: colors.text,
                  lineHeight: "1.7",
                  whiteSpace: "pre-wrap",
                  marginBottom: "12px",
                }}
              >
                {rewrite.text}
              </div>

              {rewrite.why && (
                <div
                  style={{
                    border: `1px solid ${colors.border}`,
                    background: colors.panelBg,
                    borderRadius: "14px",
                    padding: "12px",
                    marginBottom: "12px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "12px",
                      fontWeight: 800,
                      color: colors.textMuted,
                      marginBottom: "6px",
                      textTransform: "uppercase",
                      letterSpacing: "0.03em",
                    }}
                  >
                    {t(
                      "studioTools.aiHumanizerTool.labels.whyItFeelsMoreHuman",
                      "Why it feels more human"
                    )}
                  </div>
                  <div style={{ color: colors.textMuted, lineHeight: "1.6" }}>{rewrite.why}</div>
                </div>
              )}

              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "10px" }}>
                <span
                  style={{
                    background: toneColors.pillBg,
                    borderRadius: "999px",
                    padding: "6px 10px",
                    fontSize: "12px",
                    color: toneColors.pillText,
                    fontWeight: 700,
                  }}
                >
                  {t("studioTools.aiHumanizerTool.labels.score", "Score")}: {rewrite.score}
                </span>

                {rewrite.label && (
                  <span
                    style={{
                      background: colors.chipBg,
                      borderRadius: "999px",
                      padding: "6px 10px",
                      fontSize: "12px",
                      color: colors.textMuted,
                      fontWeight: 700,
                    }}
                  >
                    {rewrite.label}
                  </span>
                )}
              </div>

              {renderScoreBar(rewrite.score)}

              <ActionRow style={{ marginTop: "14px" }}>
                <CopyButton
                  copied={copiedId === index}
                  onClick={() => copyRewrite(rewrite, index)}
                >
                  {copiedId === index
                    ? t("studioTools.aiHumanizerTool.actions.copied", "Copied")
                    : t("studioTools.aiHumanizerTool.actions.copy", "Copy")}
                </CopyButton>

                <SaveStudioAssetButton
                  toolType="humanizer"
                  title={`AI Humanizer • ${sharedPlatform || "General"}`}
                  topic={sharedTopic || "Humanized rewrite"}
                  platform={sharedPlatform || "General"}
                  content={rewrite.text || ""}
                  meta={{
                    score: rewrite.score,
                    label: rewrite.label,
                    version_name: rewrite.version_name || "",
                    tone,
                    audience,
                    humanization_strength: humanizationStrength,
                    preserve_original_meaning: preserveOriginalMeaning,
                    why: rewrite.why || "",
                    original_text: originalText || "",
                  }}
                />
              </ActionRow>
            </ResultCard>
          );
        })
        )}
      </div>
    </div>
    </MotionSurface>
  );
};

export default AIHumanizerTool;